import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import {
  Header,
  Icon,
  Card,
  List,
  Label,
  Message,
  Form,
  Accordion,
} from 'semantic-ui-react';
import { SG_GET_SLACK_MEETINGS, SG_GET_SLACK_MEETING_TASKS } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function Meetings() {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  
  const { meetings, meetingTasks, meetingsPending } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [expandedMeeting, setExpandedMeeting] = useState(null);

  useEffect(() => {
    if (workspaceId) {
      fetchMeetings();
    }
  }, [selectedDate, workspaceId]);

  const fetchMeetings = () => {
    if (!workspaceId) return;
    
    const params = new URLSearchParams({ date: selectedDate }).toString();
    dispatch({
      type: SG_GET_SLACK_MEETINGS,
      payload: { workspaceId, queryParams: params },
    });
  };

  const handleMeetingClick = (meetingId) => {
    if (!workspaceId) return;
    
    if (expandedMeeting === meetingId) {
      setExpandedMeeting(null);
    } else {
      setExpandedMeeting(meetingId);
      dispatch({
        type: SG_GET_SLACK_MEETING_TASKS,
        payload: { workspaceId, meetingId },
      });
    }
  };

  const meetingList = Array.isArray(meetings) ? meetings : [];
  const tasks = Array.isArray(meetingTasks) ? meetingTasks : [];

  const getPriorityLabel = (priority) => {
    const labels = {
      1: { color: 'red', text: 'Urgent' },
      2: { color: 'orange', text: 'High' },
      3: { color: 'yellow', text: 'Medium' },
      4: { color: 'grey', text: 'Low' },
    };
    return labels[priority] || { color: 'grey', text: 'N/A' };
  };

  const getStatusIcon = (status) => {
    const icons = {
      todo: 'circle outline',
      in_progress: 'spinner',
      done: 'check circle',
    };
    return icons[status] || 'circle';
  };

  return (
    <Page>
      <Navigation />

      <Header as="h1">
        <Icon name="calendar" />
        Meetings
        <Header.Subheader>
          View meetings and their associated tasks
        </Header.Subheader>
      </Header>

      {/* Date Filter */}
      <Form>
        <Form.Group>
          <Form.Input
            label="Meeting Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            width={4}
          />
          <Form.Field width={12}>
            <label>&nbsp;</label>
            <QuickDateButtons>
              <DateBtn onClick={() => setSelectedDate(formatDate(new Date()))}>Today</DateBtn>
              <DateBtn onClick={() => setSelectedDate(formatDate(getTomorrow()))}>
                Tomorrow
              </DateBtn>
              <DateBtn onClick={() => setSelectedDate(formatDate(getNextWeek()))}>
                Next Week
              </DateBtn>
            </QuickDateButtons>
          </Form.Field>
        </Form.Group>
      </Form>

      {/* Meetings List */}
      {meetingsPending ? (
        <Message icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Loading meetings...</Message.Header>
          </Message.Content>
        </Message>
      ) : meetingList.length === 0 ? (
        <Message info>
          <Message.Header>No meetings found</Message.Header>
          <p>No meetings scheduled for {selectedDate}</p>
        </Message>
      ) : (
        <Card.Group>
          {meetingList.map((meeting) => (
            <Card fluid key={meeting.id}>
              <Card.Content onClick={() => handleMeetingClick(meeting.id)} style={{ cursor: 'pointer' }}>
                <Card.Header>
                  <Icon name="video" color="purple" />
                  {meeting.title}
                </Card.Header>
                <Card.Meta>
                  {meeting.date && new Date(meeting.date).toLocaleString()}
                </Card.Meta>
                <Card.Description>
                  <MetaInfo>
                    <Icon name="user" />
                    Created by: {meeting.createdBy?.name || 'Unknown'}
                  </MetaInfo>
                  <MetaInfo>
                    <Icon name="tasks" />
                    {meeting.taskCount || 0} tasks
                  </MetaInfo>
                </Card.Description>
              </Card.Content>

              {/* Expanded Tasks */}
              {expandedMeeting === meeting.id && (
                <Card.Content extra>
                  <Header as="h4">Meeting Tasks</Header>
                  {tasks.length === 0 ? (
                    <p>No tasks associated with this meeting</p>
                  ) : (
                    <List divided relaxed>
                      {tasks.map((task) => {
                        const priorityLabel = getPriorityLabel(task.priority);
                        return (
                          <List.Item key={task.id}>
                            <List.Content>
                              <List.Header>
                                <Icon name={getStatusIcon(task.status)} />
                                {task.title}
                              </List.Header>
                              <List.Description>
                                <TaskMeta>
                                  <Label size="mini" color={priorityLabel.color}>
                                    {priorityLabel.text}
                                  </Label>
                                  {task.assignedTo && (
                                    <span>
                                      <Icon name="user" />
                                      {task.assignedTo.name}
                                    </span>
                                  )}
                                  {task.dueDate && (
                                    <span>
                                      <Icon name="calendar" />
                                      {new Date(task.dueDate).toLocaleDateString()}
                                    </span>
                                  )}
                                </TaskMeta>
                                {task.description && (
                                  <TaskDescription>{task.description}</TaskDescription>
                                )}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        );
                      })}
                    </List>
                  )}
                </Card.Content>
              )}
            </Card>
          ))}
        </Card.Group>
      )}
    </Page>
  );
}

// Helper functions
function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function getTomorrow() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

function getNextWeek() {
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  return nextWeek;
}

const Page = styled.div`
  padding: 20px;
`;

const QuickDateButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const DateBtn = styled.button`
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #2185d0;
  color: #2185d0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #eff6ff;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  color: #666;

  i.icon {
    margin: 0;
  }
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #666;
  }

  i.icon {
    margin: 0;
  }
`;

const TaskDescription = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #666;
  font-style: italic;
`;

export default Meetings;
